// Settings for admins (spec 10): rules from 3.1, briefing recipients, assignment threshold, exclusion list; plus
// the system status (last mail, the mailboxes; out of the header since 01.11.2026; the messages "alt → neu" to the
// other apps, AB3; the calendars; who left with open work) and the quality of the assignment; the handover between
// staff (decision 01.11.2026).
import { ApiError, type AddressBookConsumerStatus, type CalendarStatus, type Exclusion, type HandoverCandidate, type HandoverCounts, type Quality, type Settings, type TaskReview } from "../api";
import { CHANCEN, creditRegeln, VERPACKUNG_LABELS, VERPACKUNGS_ARTEN, VOLUMEN_BASEN, VOLUMEN_BASIS_LABELS, VOLUMEN_FORM_LABELS, VOLUMEN_FORMEN, type CreditRegeln } from "@pipeline/core";
import { mailStatus } from "../header";
import { S } from "../state";
import { $, emptyState, esc, fmtDT, toast } from "../util";
import { tablettenHtml } from "./volumen";

const local = {
  settings: null as Settings | null,
  exclusions: [] as Exclusion[],
  quality: null as Quality | null,
  consumers: [] as AddressBookConsumerStatus[],
  calendar: null as CalendarStatus | null,
  leavers: [] as HandoverCandidate[],
  review: null as TaskReview | null,
  loading: false,
};

const OUTCOME: Record<"erledigt" | "ersetzen" | "offen", string> = { erledigt: "erledigt", ersetzen: "ersetzt", offen: "bleibt offen" };

/** The AI's review of the stale HubSpot tasks: what waits, the last run with counts and per deal what changed. */
export function reviewPanel(r: TaskReview | null): string {
  if (!r) return "";
  const run = r.run;
  const busy = run && (run.status === "queued" || run.status === "running");
  const c = run?.counts;
  const counts = c ? `<p style="margin:8px 0 0">${n(c.erledigt ?? 0, "Aufgabe erledigt", "Aufgaben erledigt")}, ${c.ersetzen ?? 0} ersetzt, ${c.offen ?? 0} bleiben offen${c.uebersprungen ? `; ${c.uebersprungen} an Deals mit eigenen Aufgaben nicht angefasst` : ""}${c.fehler ? `; ${c.fehler} nicht geprüft (Fehler)` : ""}.</p>` : "";
  const deals = (run?.report?.deals ?? [])
    .map(
      (d) =>
        `<li><a class="link" href="#deal-${esc(d.dealId)}"><span class="mono">${esc(d.ref)}</span> ${esc(d.company)} · ${esc(d.name)}</a>${d.changes
          .map((x) => `<span class="why"><b>${esc(OUTCOME[x.outcome])}:</b> ${esc(x.task)} <span class="muted">(fällig ${esc(x.due)})</span> – ${esc(x.reason)}${x.newStep ? ` <b>Neu:</b> ${esc(x.newStep)}` : ""}</span>`)
          .join("")}</li>`,
    )
    .join("");
  return `<div class="panel" id="task-review"><h2>Alte HubSpot-Aufgaben prüfen</h2>
    <p class="muted" style="margin:0 0 8px;font-size:12.5px">Claude liest bei jeder überfälligen Aufgabe aus dem HubSpot-Import, was seitdem am Deal geschah: erledigt, durch einen neuen Schritt ersetzt oder offen. An den Kunden geht nichts; Deals mit Aufgaben aus der Pipeline bleiben unberührt.</p>
    <p style="margin:0">${r.waiting ? `${n(r.waiting, "Aufgabe wartet", "Aufgaben warten")} auf die Prüfung.` : "Keine ungeprüfte Aufgabe."}</p>
    <div class="actions" style="margin-top:8px"><button class="btn primary" type="button" data-review-start ${busy || !r.waiting ? "disabled" : ""}>${busy ? "Prüfung läuft …" : "Prüfung starten"}</button>${busy ? "" : `<button class="link" type="button" data-review-force>Alle noch einmal prüfen</button>`}</div>
    ${run ? `<p class="muted" style="margin:10px 0 0;font-size:12.5px">Letzter Lauf ${fmtDT(run.createdAt)}${run.status === "failed" ? `, abgebrochen: ${esc(run.error ?? "")}` : run.finishedAt ? `, fertig ${fmtDT(run.finishedAt)}` : ""}</p>${counts}` : ""}
    ${deals ? `<ul class="rows brows">${deals}</ul>` : ""}</div>`;
}
/** The handover being prepared: who to whom, and the counts the preview showed. */
const handover = { from: "", to: "", preview: null as (HandoverCounts & { from: string; to: string }) | null, busy: false };

const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** Someone left the groups but still has open deals or tasks: a hint with the way to the handover. */
export function leaverLines(leavers: HandoverCandidate[]): string {
  return leavers
    .map(
      (l) =>
        `<p class="note warn" style="margin:8px 0 0">${esc(l.name)} ist nicht mehr in ${esc(l.left.join(", "))}, hat aber noch ${[l.deals ? n(l.deals, "offenen Deal", "offene Deals") : "", l.tasks ? n(l.tasks, "offene Aufgabe", "offene Aufgaben") : ""].filter(Boolean).join(" und ")}. <button class="link" data-handover-from="${esc(l.id)}">Übergeben …</button></p>`,
    )
    .join("");
}

function handoverPanel(): string {
  const options = (selected: string) => `<option value="">–</option>${S.users.map((u) => `<option value="${esc(u.id)}" ${u.id === selected ? "selected" : ""}>${esc(u.name)}</option>`).join("")}`;
  const p = handover.preview;
  const summary = p
    ? `<p style="margin:8px 0 0">Von ${esc(p.from)} an ${esc(p.to)}: ${n(p.deals, "offener Deal", "offene Deals")}, ${n(p.tasks, "offene Aufgabe", "offene Aufgaben")}${p.meetings ? `, dazu ${n(p.meetings, "künftiger Termin", "künftige Termine")} zum Übernehmen (bleiben in Outlook bei ${esc(p.from)}; ${esc(p.to)} bekommt je eine Aufgabe)` : ""}.</p>
      <div class="actions" style="margin-top:8px"><button class="btn primary" type="button" data-handover-run ${handover.busy || !(p.deals || p.tasks || p.meetings) ? "disabled" : ""}>Übergabe ausführen</button></div>`
    : "";
  return `<div class="panel" id="handover"><h2>Übergabe zwischen Mitarbeitern</h2>
    <p class="muted" style="margin:0 0 8px;font-size:12.5px">Offene Deals und offene Aufgaben wechseln in einem Schritt; abgeschlossene Deals und Erledigtes bleiben, wie sie sind.</p>
    <form class="excl-form" id="handover-form"><select name="from" aria-label="Von">${options(handover.from)}</select><select name="to" aria-label="An">${options(handover.to)}</select><button class="btn">Vorschau</button></form>${summary}</div>`;
}

/** The calendars of Pipeline.Kalender: off, waiting for the permission, or read; one line each, one note if it lacks. */
export function calendarLines(c: CalendarStatus | null): string {
  if (!c) return "";
  const live = c.owners.filter((o) => !o.endedAt);
  if (!c.enabled) return `<ul class="rows" style="margin-top:10px"><li><span>Outlook-Kalender</span><span class="r">ausgeschaltet</span></li></ul>`;
  const state = (o: CalendarStatus["owners"][number]) =>
    o.status === "no_permission" ? "keine Berechtigung" : o.status === "error" ? "Fehler" : o.lastSyncAt ? `gelesen ${fmtDT(o.lastSyncAt)}` : "noch nicht gelesen";
  const denied = live.filter((o) => o.status === "no_permission");
  return `<ul class="rows" style="margin-top:10px">${live.map((o) => `<li><span>Kalender ${esc(o.name ?? o.address)}</span><span class="r">${esc(state(o))}</span></li>`).join("") || `<li><span>Outlook-Kalender</span><span class="r">niemand in Pipeline.Kalender</span></li>`}</ul>
    ${denied.length ? `<p class="note warn" style="margin:0 0 8px">Die Pipeline darf ${denied.length === 1 ? "einen Kalender" : `${denied.length} Kalender`} seit ${fmtDT(denied[0]!.statusSince)} nicht lesen: Calendars.ReadWrite für Pipeline.Kalender ist in Exchange noch nicht zugewiesen (infra/exchange-rbac.ps1).</p>` : ""}
    ${live.filter((o) => o.status === "error").map((o) => `<p class="note crit" style="margin:0 0 8px">${esc(o.name ?? o.address)}: ${esc(o.lastError ?? "")}</p>`).join("")}
    <p class="muted" style="margin:6px 0 0;font-size:12.5px">Kundentermine aus den Kalendern von Pipeline.Kalender, alle 15 Minuten; private Termine nie.</p>`;
}

const APP: Record<AddressBookConsumerStatus["consumer"], string> = { auftrag: "auftrag", angebot: "Ange.Bot" };

/** One line per subscribed app: waiting for its address, failing, on the way, or all delivered. */
function consumerLines(): string {
  if (!local.consumers.length) return "";
  const state = (c: AddressBookConsumerStatus) =>
    !c.reachable
      ? `wartet auf die Adresse${c.open ? ` · ${c.open} offen` : ""}`
      : c.failing
        ? `${c.open} offen`
        : c.open
          ? `${c.open} unterwegs`
          : c.lastDeliveredAt
            ? `zugestellt ${fmtDT(c.lastDeliveredAt)}`
            : "noch keine";
  const failing = local.consumers.filter((c) => c.reachable && c.failing);
  return `<ul class="rows" style="margin-top:10px">${local.consumers.map((c) => `<li><span>Meldungen an ${esc(APP[c.consumer])}</span><span class="r">${esc(state(c))}</span></li>`).join("")}</ul>
    ${failing.map((c) => `<p class="note crit" style="margin:0 0 8px">${esc(APP[c.consumer])} nimmt ${c.failing === 1 ? "eine Meldung" : `${c.failing} Meldungen`} seit ${fmtDT(c.failingSince)} nicht an: ${esc(c.lastError ?? "ohne Fehlertext")}</p>`).join("")}
    ${local.consumers
      .filter((c) => c.rejected)
      .map((c) => `<p class="note crit" style="margin:0 0 8px">${esc(APP[c.consumer])} hat ${c.rejected === 1 ? "eine Meldung" : `${c.rejected} Meldungen`} abgewiesen; sie sind geparkt und werden nicht wiederholt: ${esc(c.lastRejection ?? "ohne Fehlertext")}</p>`)
      .join("")}
    ${local.consumers
      .filter((c) => c.conflicts)
      .map((c) => `<p class="note" style="margin:0 0 8px">${esc(APP[c.consumer])} meldet bei ${c.conflicts === 1 ? "einer Zusammenführung" : `${c.conflicts} Zusammenführungen`} einen Konflikt; die Kundenbetreuung dort ist informiert.</p>`)
      .join("")}
    <p class="muted" style="margin:6px 0 0;font-size:12.5px">Zusammengeführte Firmen, Personen und Adressen (alt → neu); offene gehen raus, sobald die App erreichbar ist.</p>`;
}

const lines = (v: string) => v.split("\n").map((s) => s.trim()).filter(Boolean);

export async function loadSettings(): Promise<void> {
  if (!S.me?.isAdmin || local.loading) return;
  local.loading = true;
  try {
    [local.settings, local.exclusions, local.quality, local.consumers, local.calendar] = await Promise.all([
      S.api.settings(),
      S.api.exclusions(),
      S.api.quality(),
      S.api.addressBookConsumers().catch(() => []),
      S.api.calendarStatus().catch(() => null),
    ]);
    local.leavers = await S.api.handoverCandidates().catch(() => []);
    local.review = await S.api.taskReview().catch(() => null);
  } finally {
    local.loading = false;
  }
}

/** Unsaved edits in the settings forms: kept over redraws until saved. */
let dirty = false;

export function renderSettings(): void {
  const el = $("#v-einstellungen");
  if (dirty && el.querySelector("form")) return;
  if (!S.me?.isAdmin) {
    el.innerHTML = emptyState("Nur für Admins", "Die Einstellungen kann nur ändern, wer in der Gruppe Pipeline.Admin ist.");
    return;
  }
  const s = local.settings;
  if (!s) {
    el.innerHTML = emptyState("Lade Einstellungen …", "");
    void loadSettings().then(renderSettings);
    return;
  }
  const q = local.quality;
  const r = s.rules;
  const st = mailStatus(S.meta);
  const fill = Object.entries(r.defaultFillMg).map(([k, v]) => `${k}: ${v}`).join("\n");
  el.innerHTML = `<div class="brief-head"><span class="eyebrow">Einstellungen</span><h1>Regeln und Ausschlüsse</h1>
      <p>Änderungen gelten sofort für neue Mails und Bewertungen.</p></div>
    <div class="settings">
      <form class="panel set-form" id="rules-form">
        <h2>Leistungskatalog</h2>
        <label>Mindestmenge je Charge (kg)<input name="minBatchKg" type="number" min="1" step="any" value="${r.minBatchKg}"></label>
        <label>Solida (eine je Zeile)<textarea name="solids" rows="5">${esc(r.solids.join("\n"))}</textarea></label>
        <label>Liquida, ausgeschlossen (eine je Zeile)<textarea name="liquids" rows="4">${esc(r.liquids.join("\n"))}</textarea></label>
        <label>No-Go-Stoffe (einer je Zeile)<textarea name="noGo" rows="4">${esc(r.noGo.join("\n"))}</textarea></label>
        <label>Packmittel (eines je Zeile; Unbekanntes wird Prüfpunkt „?“)<textarea name="packaging" rows="4">${esc(r.packaging.join("\n"))}</textarea></label>
        <label>Standard-Füllgewichte in mg („Form: mg“ je Zeile)<textarea name="fill" rows="5">${esc(fill)}</textarea></label>
        <h2>Volumen-Credits</h2>
        <p class="muted" style="margin:0;font-size:12.5px">Gewichten das geschätzte Jahresvolumen eines Deals: Credits je 1.000 Stück und je kg der Form und je 1.000 Packmittel (gedacht als Innenumsatz, etwa 10 € je Credit), daraus die Wertigkeit von 1 bis 5 Tabletten und der Platz unter den offenen Deals.</p>
        ${creditsTable(r.volumenCredits)}
        <h2>Briefing und Zuordnung</h2>
        <label>Briefing-Empfänger (eine Adresse je Zeile)<textarea name="recipients" rows="3">${esc(s.briefingRecipients.join("\n"))}</textarea></label>
        <label>Schwelle für automatische Zuordnung (0,5–0,99)<input name="threshold" type="number" min="0.5" max="0.99" step="0.01" value="${s.assignThreshold}"></label>
        <div><button class="btn primary">Speichern</button></div>
      </form>
      <div class="side">
        <div class="panel"><h2>Systemstatus</h2>
          ${st ? `<p class="sysline"><span class="dot ${st.level}"></span><span>${esc(st.text)}</span></p>${st.level === "ok" ? "" : `<p class="note ${st.level}" style="margin:0 0 8px">${esc(st.detail)}</p>`}` : ""}
          <ul class="rows">${(S.meta?.mailboxes ?? []).map((m) => `<li><span>${esc(m.address)}</span><span class="r mono">${m.lastIngestAt ? fmtDT(m.lastIngestAt) : "–"}</span></li>`).join("")}</ul>
          <p class="muted" style="margin:6px 0 0;font-size:12.5px">Zeit der letzten verarbeiteten Mail je Postfach.</p>${consumerLines()}${calendarLines(local.calendar)}${leaverLines(local.leavers)}</div>
        ${handoverPanel()}
        ${reviewPanel(local.review)}
        <div class="panel"><h2>Qualität der Zuordnung</h2>
          ${q && q.automatic ? `<p style="margin:0"><b>${Math.round((q.hitRate ?? 0) * 100)} %</b> der automatischen Zuordnungen der letzten ${q.days} Tage sind unverändert (${q.corrected} von ${q.automatic} mit „👎 falsch zugeordnet“ korrigiert).</p>` : `<p class="muted" style="margin:0">Noch keine automatischen Zuordnungen in den letzten ${q?.days ?? 30} Tagen.</p>`}
          ${q ? `<p class="muted" style="margin:6px 0 0;font-size:12.5px">Was niemand korrigiert, gilt als richtig. Dazu ${q.klaerfaelle} ${q.klaerfaelle === 1 ? "Mail" : "Mails"} ohne sichere Zuordnung im selben Zeitraum.</p>` : ""}</div>
        <div class="panel"><h2>Ausschlussliste</h2>
          <p class="muted" style="margin:0 0 8px;font-size:12.5px">Mails dieser Absender werden nie verarbeitet, nur als Hash vermerkt.</p>
          ${local.exclusions.length ? `<ul class="rows">${local.exclusions.map((x) => `<li><span>${esc(x.value)} <small class="muted">${x.kind === "domain" ? "Domain" : "Adresse"}${x.note ? ` · ${esc(x.note)}` : ""}</small></span><button class="link" data-unexclude="${esc(x.value)}">Entfernen</button></li>`).join("")}</ul>` : `<p class="muted" style="margin:0">Keine Einträge.</p>`}
          <form class="excl-form" id="excl-form">
            <input name="value" placeholder="adresse@firma.example oder firma.example" required>
            <select name="kind" aria-label="Art"><option value="domain">Domain</option><option value="address">Adresse</option></select>
            <input name="note" placeholder="Notiz (optional)">
            <button class="btn">Hinzufügen</button>
          </form></div>
      </div>
    </div>`;
}

/** One row per form with its credits per 1,000 pieces and per kg, then the thresholds for 2 to 5 tablets. */
export function creditsTable(stored: CreditRegeln): string {
  // Values missing in older settings show their defaults.
  const c = creditRegeln(stored);
  const input = (name: string, value: number, label: string) => `<input name="${name}" type="number" min="0" step="any" required value="${value}" aria-label="${esc(label)}">`;
  const pctInput = (name: string, value: number, label: string) => `<input name="${name}" type="number" min="0" max="100" step="1" required value="${Math.round(value * 100)}" aria-label="${esc(label)}">`;
  return `<div class="credits"><table><thead><tr><th>Form</th><th>je 1.000 Stück</th><th>je kg</th></tr></thead><tbody>${VOLUMEN_FORMEN.map(
    (f) =>
      `<tr><td>${esc(VOLUMEN_FORM_LABELS[f])}</td><td>${input(`vc-${f}-stueck`, c.faktoren[f].stueck, `${VOLUMEN_FORM_LABELS[f]}: Credits je 1.000 Stück`)}</td><td>${input(`vc-${f}-kg`, c.faktoren[f].kg, `${VOLUMEN_FORM_LABELS[f]}: Credits je kg`)}</td></tr>`,
  ).join("")}${VERPACKUNGS_ARTEN.map(
    (a) => `<tr><td>${esc(VERPACKUNG_LABELS[a])}</td><td>${input(`vp-${a}`, c.verpackung[a], `${VERPACKUNG_LABELS[a]}: Credits je 1.000 Stück`)}</td><td class="muted">–</td></tr>`,
  ).join("")}</tbody></table>
    <p class="credits-share"><b>Sicherheit der Menge</b> (Anteil, mit dem sie zählt)${VOLUMEN_BASEN.map((b) => `<label>${esc(VOLUMEN_BASIS_LABELS[b])}${pctInput(`vs-sicher-${b}`, c.sicherheit[b], `${VOLUMEN_BASIS_LABELS[b]}: Anteil in Prozent`)} %</label>`).join("")}</p>
    <p class="credits-share"><b>Chance</b> (Anteil für die Erwartung und den Platz)${CHANCEN.map((x) => `<label>${x}${pctInput(`vs-chance-${x}`, c.chance[x], `Chance ${x}: Anteil in Prozent`)} %</label>`).join("")}</p>
    <p class="credits-tabs">${c.sterne.map((t, i) => `<label>${tablettenHtml(i + 2)} ab${input(`vs-${i}`, t, `${i + 2} Tabletten ab Credits`)}</label>`).join("")}</p></div>`;
}

/** The credits from the form; null when the thresholds do not rise. */
function readCredits(v: (name: string) => string): CreditRegeln | null {
  const faktoren = Object.fromEntries(VOLUMEN_FORMEN.map((f) => [f, { stueck: Number(v(`vc-${f}-stueck`)), kg: Number(v(`vc-${f}-kg`)) }])) as CreditRegeln["faktoren"];
  const verpackung = Object.fromEntries(VERPACKUNGS_ARTEN.map((a) => [a, Number(v(`vp-${a}`))])) as CreditRegeln["verpackung"];
  const sterne = [0, 1, 2, 3].map((i) => Number(v(`vs-${i}`))) as CreditRegeln["sterne"];
  const share = (name: string) => Math.min(1, Math.max(0, Number(v(name)) / 100));
  const sicherheit = Object.fromEntries(VOLUMEN_BASEN.map((b) => [b, share(`vs-sicher-${b}`)])) as CreditRegeln["sicherheit"];
  const chance = Object.fromEntries(CHANCEN.map((x) => [x, share(`vs-chance-${x}`)])) as CreditRegeln["chance"];
  return sterne.every((t, i) => i === 0 || t >= sterne[i - 1]!) ? { faktoren, verpackung, sicherheit, chance, sterne } : null;
}

function readFill(text: string): Record<string, number> | null {
  const out: Record<string, number> = {};
  for (const l of lines(text)) {
    const m = /^(.+?):\s*([\d.,]+)$/.exec(l);
    if (!m) return null;
    out[m[1]!.trim()] = Number(m[2]!.replace(",", "."));
  }
  return out;
}

export function bindSettings(): void {
  document.addEventListener("input", (e) => {
    if ((e.target as HTMLElement).closest("#v-einstellungen form:not(#handover-form)")) dirty = true;
  });
  document.addEventListener("submit", async (e) => {
    const f = e.target as HTMLFormElement;
    if (f.id === "handover-form") {
      e.preventDefault();
      handover.from = (f.elements.namedItem("from") as HTMLSelectElement).value;
      handover.to = (f.elements.namedItem("to") as HTMLSelectElement).value;
      handover.preview = null;
      if (!handover.from || !handover.to || handover.from === handover.to) return toast("Bitte zwei verschiedene Personen wählen.");
      try {
        handover.preview = await S.api.handoverPreview(handover.from, handover.to);
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Die Vorschau ging nicht.");
      }
      return renderSettings();
    }
    if (f.id !== "rules-form" && f.id !== "excl-form") return;
    e.preventDefault();
    dirty = false;
    const v = (name: string) => (f.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement).value;
    try {
      if (f.id === "rules-form") {
        const fill = readFill(v("fill"));
        if (!fill) return toast("Füllgewichte bitte als „Form: mg“, eines je Zeile.");
        const volumenCredits = readCredits(v);
        if (!volumenCredits) return toast("Die Schwellen für 2 bis 5 Tabletten müssen ansteigen.");
        local.settings = await S.api.saveSettings({
          rules: { minBatchKg: Number(v("minBatchKg")), solids: lines(v("solids")), liquids: lines(v("liquids")), noGo: lines(v("noGo")), packaging: lines(v("packaging")), defaultFillMg: fill, volumenCredits },
          briefingRecipients: lines(v("recipients")),
          assignThreshold: Number(v("threshold")),
        });
        // The volume overview weighs with the new credits right away, not only after the next poll.
        if (S.meta) S.meta.rules = local.settings.rules;
        toast("Einstellungen gespeichert");
      } else {
        await S.api.addExclusion({ value: v("value"), kind: v("kind") as "address" | "domain", note: v("note") || undefined });
        local.exclusions = await S.api.exclusions();
        toast("Zur Ausschlussliste hinzugefügt");
      }
      renderSettings();
    } catch (err) {
      // Not saved: the edits stay in the form.
      dirty = true;
      toast(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    }
  });
  document.addEventListener("click", async (e) => {
    const rv = (e.target as HTMLElement).closest<HTMLElement>("#v-einstellungen [data-review-start], #v-einstellungen [data-review-force]");
    if (rv) {
      const force = rv.dataset.reviewForce !== undefined;
      if (force && !confirm("Alle überfälligen HubSpot-Aufgaben noch einmal von Claude prüfen lassen, auch die schon geprüften?")) return;
      try {
        await S.api.startTaskReview(force);
        toast("Die Prüfung läuft im Hintergrund; der Bericht steht gleich hier.");
        local.review = await S.api.taskReview().catch(() => local.review);
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Die Prüfung ließ sich nicht starten.");
      }
      return renderSettings();
    }
    const h = (e.target as HTMLElement).closest<HTMLElement>("#v-einstellungen [data-handover-from], #v-einstellungen [data-handover-run]");
    if (h?.dataset.handoverFrom) {
      Object.assign(handover, { from: h.dataset.handoverFrom, to: "", preview: null });
      renderSettings();
      return void document.querySelector("#handover")?.scrollIntoView({ block: "center" });
    }
    if (h && handover.preview && !handover.busy) {
      const p = handover.preview;
      if (!confirm(`${p.deals} Deals und ${p.tasks} Aufgaben von ${p.from} an ${p.to} übergeben?`)) return;
      handover.busy = true;
      renderSettings();
      try {
        const done = await S.api.handover(handover.from, handover.to, { deals: p.deals, tasks: p.tasks, meetings: p.meetings });
        toast(`Übergeben: ${n(done.deals, "Deal", "Deals")}, ${n(done.tasks, "Aufgabe", "Aufgaben")}${done.meetings ? `, ${n(done.meetings, "Termin", "Termine")} zum Übernehmen` : ""}.`);
        Object.assign(handover, { from: "", to: "", preview: null });
        local.leavers = await S.api.handoverCandidates().catch(() => []);
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Die Übergabe ging nicht.");
        handover.preview = null;
      } finally {
        handover.busy = false;
        renderSettings();
      }
      return;
    }
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-unexclude]");
    if (!t) return;
    try {
      await S.api.deleteExclusion(t.dataset.unexclude!);
      local.exclusions = await S.api.exclusions();
      toast("Entfernt");
      renderSettings();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Entfernen fehlgeschlagen.");
    }
  });
}
